import { useState, useCallback, useEffect } from 'react';
import {
  Box, Typography, IconButton, Chip, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Paper, Autocomplete, TextField,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Add as AddTagIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import {
  useRemoveDocumentMutation,
  useAddDocumentTagMutation,
  useRemoveDocumentTagMutation,
} from '../../../../../services/MedNecService';
import { useGetDocumentUrlQuery } from '../../../../../services/MedNecService/medNecApi';
import type { DocumentView } from '../../../../../services/MedNecService/types/medNecTypes';
import { DOCUMENT_TYPES, TYPE_COLORS } from './documentTypes';

interface DocumentListProps {
  caseId: string;
  documents: DocumentView[];
  allowActions: boolean;
}

const DocumentList: React.FC<DocumentListProps> = ({ caseId, documents, allowActions }) => {
  const [removeDocument] = useRemoveDocumentMutation();
  const [addDocumentTag] = useAddDocumentTagMutation();
  const [removeDocumentTag] = useRemoveDocumentTagMutation();
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [addTagDocId, setAddTagDocId] = useState<string | null>(null);
  const [newTag, setNewTag] = useState<string | null>(null);
  const [viewDocId, setViewDocId] = useState<string | null>(null);

  const { data: docUrl } = useGetDocumentUrlQuery(
    { id: caseId, documentId: viewDocId ?? '' },
    { skip: !viewDocId },
  );

  // Open SAS URL when it arrives
  useEffect(() => {
    if (docUrl?.sasUrl && viewDocId) {
      window.open(docUrl.sasUrl, '_blank', 'noopener,noreferrer');
      setViewDocId(null);
    }
  }, [docUrl?.sasUrl, viewDocId]);

  const handleRemove = useCallback(async () => {
    if (!confirmDelete) return;
    await removeDocument({ id: caseId, documentId: confirmDelete });
    setConfirmDelete(null);
  }, [confirmDelete, caseId, removeDocument]);

  const handleAddTag = useCallback(async () => {
    if (!addTagDocId || !newTag) return;
    await addDocumentTag({ id: caseId, documentId: addTagDocId, tag: newTag });
    setAddTagDocId(null);
    setNewTag(null);
  }, [addTagDocId, newTag, caseId, addDocumentTag]);

  const handleRemoveTag = useCallback(async (documentId: string, tag: string) => {
    await removeDocumentTag({ id: caseId, documentId, tag });
  }, [caseId, removeDocumentTag]);

  // Compute available tags for the add-tag dialog (exclude already-assigned)
  const addTagDoc = documents.find((d) => d.documentId === addTagDocId);
  const availableTags = addTagDoc
    ? [...DOCUMENT_TYPES].filter((t) => !addTagDoc.documentTypes.includes(t))
    : [...DOCUMENT_TYPES];

  if (documents.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 3, textAlign: 'center', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2 }}>
        <FileIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
        <Typography variant="body2" sx={{ color: '#64748b' }}>
          No documents uploaded yet
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {documents.map((doc) => {
          const primaryColor = TYPE_COLORS[doc.documentTypes[0]] ?? '#64748b';
          return (
            <Paper
              key={doc.documentId}
              elevation={0}
              sx={{
                display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.5,
                backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 1.5,
                '&:hover': { borderColor: '#94a3b8' },
              }}
            >
              <FileIcon sx={{ color: primaryColor, fontSize: 24 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {doc.fileName}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  {new Date(doc.uploadedAt).toLocaleDateString()} &middot; {doc.uploadedBy}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {doc.documentTypes.map((tag) => (
                  <Chip
                    key={tag}
                    size="small"
                    label={tag}
                    onDelete={allowActions && doc.documentTypes.length > 1 ? () => handleRemoveTag(doc.documentId, tag) : undefined}
                    sx={{
                      fontWeight: 600, fontSize: '0.7rem',
                      backgroundColor: `${TYPE_COLORS[tag] ?? '#64748b'}15`,
                      color: TYPE_COLORS[tag] ?? '#64748b',
                      border: `1px solid ${TYPE_COLORS[tag] ?? '#64748b'}40`,
                    }}
                  />
                ))}
              </Box>

              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="View">
                  <IconButton size="small" onClick={() => setViewDocId(doc.documentId)}>
                    <ViewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {allowActions && (
                  <>
                    <Tooltip title="Add tag">
                      <IconButton size="small" onClick={() => { setAddTagDocId(doc.documentId); setNewTag(null); }}>
                        <AddTagIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove document">
                      <IconButton size="small" onClick={() => setConfirmDelete(doc.documentId)} sx={{ color: '#ef4444' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            </Paper>
          );
        })}
      </Box>

      {/* Delete confirmation dialog */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Remove Document</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to remove this document? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button onClick={handleRemove} color="error" variant="contained">Remove</Button>
        </DialogActions>
      </Dialog>

      {/* Add tag dialog */}
      <Dialog open={!!addTagDocId} onClose={() => setAddTagDocId(null)}>
        <DialogTitle>Add Document Tag</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Select a tag to add:</Typography>
          <Autocomplete
            options={availableTags}
            value={newTag}
            onChange={(_, value) => setNewTag(value)}
            size="small"
            renderInput={(params) => <TextField {...params} placeholder="Select tag" />}
            sx={{ minWidth: 280 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddTagDocId(null)}>Cancel</Button>
          <Button onClick={handleAddTag} variant="contained" disabled={!newTag}>Add</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentList;
